/**
 * POS Daily Transaction Report
 *
 * Shows summary cards, shift details, transaction table with expandable rows,
 * and CSV export. Follows the "65-year-old easy" design pattern.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPacificToday } from '../../utils/dateUtils';
import StoreHeader from '../../components/Layout/StoreHeader';
import { posAPI, PettyCashWithdrawal } from '../../services/storeAPI';
import { printTillReport } from '../../services/receiptPrinter';

interface DailyReportData {
  summary: {
    total_sales: number;
    total_tax: number;
    total_discounts: number;
    total_surcharges: number;
    transaction_count: number;
    refund_total: number;
    refund_count: number;
    net_sales: number;
    taxable_sales: number;
    exempt_sales: number;
    exempt_count: number;
    completed_count: number;
    voided_count: number;
    in_progress_count: number;
    total_all_count: number;
  };
  payment_breakdown: {
    cash: number;
    credit: number;
    debit: number;
    check: number;
    account?: number;
  };
  tab_paydowns?: number;
  sessions: Array<{
    id: number;
    cashier: string;
    opened_at: string;
    closed_at: string | null;
    status: string;
    opening_cash: number;
    closing_cash: number | null;
    expected_cash: number | null;
    cash_variance: number | null;
    opening_check_float: number;
    closing_check_count: number | null;
    expected_checks: number | null;
    check_variance: number | null;
    petty_cash_total?: number;
  }>;
  petty_cash_total: number;
  petty_cash_count: number;
  petty_cash_withdrawals: Array<{
    id: number;
    session_id: number;
    time: string;
    category: string;
    category_display: string;
    description: string;
    withdrawn_by: string;
    amount: number;
  }>;
  transactions: Array<{
    id: number;
    transaction_number: string;
    date: string;
    time: string;
    cashier: string;
    item_count: number;
    subtotal: number;
    tax: number;
    discount: number;
    surcharge: number;
    total: number;
    status: string;
    payment_methods: string[];
    refund_status: string | null;
    tax_exempt: boolean;
    customer_name: string | null;
    items: Array<{
      name: string;
      quantity: number;
      unit_price: number;
      total: number;
      is_tax_exempt: boolean;
      tax_amount: number;
      tax_rate: number;
    }>;
  }>;
  cashiers: Array<{ id: number; name: string }>;
}

const POSDailyReport: React.FC = () => {
  const navigate = useNavigate();
  const today = getPacificToday();

  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [selectedCashier, setSelectedCashier] = useState('');
  const [data, setData] = useState<DailyReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedTxn, setExpandedTxn] = useState<number | null>(null);
  const [reprintingSession, setReprintingSession] = useState<number | null>(null);
  const [taxFilter, setTaxFilter] = useState<'all' | 'taxable' | 'exempt'>('all');

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await posAPI.getDailyTransactions({
        date_from: dateFrom,
        date_to: dateTo,
        cashier: selectedCashier || undefined,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const handleReprintTillReport = async (sessionId: number) => {
    try {
      setReprintingSession(sessionId);
      const report = await posAPI.getCloseReport(sessionId);
      await printTillReport(report);
    } catch (err: any) {
      setError(err.message || 'Failed to print till report');
    } finally {
      setReprintingSession(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      await posAPI.exportDailyTransactionsCSV({
        date_from: dateFrom,
        date_to: dateTo,
        cashier: selectedCashier || undefined,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to export CSV');
    }
  };

  const formatPaymentMethods = (methods: string[]) => {
    const labels: Record<string, string> = { cash: 'Cash', credit: 'Credit', debit: 'Debit', check: 'Check', on_account: 'Account' };
    return methods.map(m => labels[m] || m).join(', ');
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#2e7d32';
      case 'voided': return '#d32f2f';
      case 'refunded': return '#e65100';
      case 'in_progress': return '#1565c0';
      default: return '#666';
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <StoreHeader />

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '1rem' }}>
        {/* Back Button + Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <button
            onClick={() => navigate('/store/pos')}
            style={{
              padding: '0.5rem 1rem', fontSize: '1rem', backgroundColor: '#e0e0e0',
              border: 'none', borderRadius: '6px', cursor: 'pointer', minHeight: '48px',
            }}
          >
            Back to POS
          </button>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Daily Transaction Report</h1>
        </div>

        {/* Filter Bar */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end',
          padding: '1rem', backgroundColor: '#fff', borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem',
        }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '6px', minHeight: '48px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '6px', minHeight: '48px' }}
            />
          </div>
          {data?.cashiers && data.cashiers.length > 0 && (
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>Cashier</label>
              <select
                value={selectedCashier}
                onChange={(e) => setSelectedCashier(e.target.value)}
                style={{ padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '6px', minHeight: '48px' }}
              >
                <option value="">All Cashiers</option>
                {data.cashiers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>Tax Status</label>
            <select
              value={taxFilter}
              onChange={(e) => setTaxFilter(e.target.value as 'all' | 'taxable' | 'exempt')}
              style={{ padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '6px', minHeight: '48px' }}
            >
              <option value="all">All</option>
              <option value="taxable">Taxable Only</option>
              <option value="exempt">Tax Exempt Only</option>
            </select>
          </div>
          <button
            onClick={loadReport}
            disabled={loading}
            style={{
              padding: '0.5rem 1.5rem', fontSize: '1rem', backgroundColor: '#1a4d1a',
              color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer',
              minHeight: '48px', opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
          <button
            onClick={handleExportCSV}
            disabled={!data || loading}
            style={{
              padding: '0.5rem 1.5rem', fontSize: '1rem', backgroundColor: '#1565c0',
              color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer',
              minHeight: '48px', opacity: !data || loading ? 0.6 : 1,
            }}
          >
            Export CSV
          </button>
        </div>

        {error && (
          <div style={{ padding: '0.75rem', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '6px', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Summary Cards */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '0.75rem', marginBottom: '1rem',
            }}>
              {[
                { label: 'Total Sales', value: `$${data.summary.total_sales.toFixed(2)}`, color: '#1a4d1a' },
                { label: 'Transactions', value: data.summary.transaction_count.toString(), color: '#333' },
                { label: 'Cash', value: `$${data.payment_breakdown.cash.toFixed(2)}`, color: '#2e7d32' },
                { label: 'Check', value: `$${data.payment_breakdown.check.toFixed(2)}`, color: '#6a1b9a' },
                { label: 'Credit', value: `$${data.payment_breakdown.credit.toFixed(2)}`, color: '#1565c0' },
                { label: 'Debit', value: `$${data.payment_breakdown.debit.toFixed(2)}`, color: '#00838f' },
                ...((data.payment_breakdown.account ?? 0) !== 0 ? [{ label: 'On Account', value: `$${(data.payment_breakdown.account ?? 0).toFixed(2)}`, color: '#8d6e63' }] : []),
                ...((data.tab_paydowns ?? 0) > 0 ? [{ label: 'Tab Pay-downs (memo)', value: `$${(data.tab_paydowns ?? 0).toFixed(2)}`, color: '#666' }] : []),
                { label: 'Tax', value: `$${data.summary.total_tax.toFixed(2)}`, color: '#e65100' },
                { label: 'Discounts', value: `$${data.summary.total_discounts.toFixed(2)}`, color: '#ad1457' },
                { label: 'Refunds', value: `-$${data.summary.refund_total.toFixed(2)}`, color: '#d32f2f' },
                ...(data.petty_cash_total > 0 ? [{ label: 'Petty Cash Out', value: `-$${data.petty_cash_total.toFixed(2)}`, color: '#508590' }] : []),
                { label: 'Net Sales', value: `$${data.summary.net_sales.toFixed(2)}`, color: '#1a4d1a' },
              ].map((card) => (
                <div key={card.label} style={{
                  backgroundColor: '#fff', padding: '1rem', borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>{card.label}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: card.color }}>{card.value}</div>
                </div>
              ))}
            </div>

            {/* Tax Summary */}
            <div style={{
              backgroundColor: '#fff', padding: '1rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem',
            }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>Tax Summary</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>Taxable Sales</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>${data.summary.taxable_sales.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>Tax-Exempt Sales</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    ${data.summary.exempt_sales.toFixed(2)}
                    <span style={{ fontSize: '0.85rem', fontWeight: 'normal', color: '#666', marginLeft: '0.5rem' }}>
                      ({data.summary.exempt_count} transaction{data.summary.exempt_count !== 1 ? 's' : ''})
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>Tax Collected</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#e65100' }}>${data.summary.total_tax.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>Surcharges (Fee Saver)</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>${data.summary.total_surcharges.toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Data Integrity */}
            {(() => {
              const s = data.summary;
              const expectedTotal = s.completed_count + s.voided_count + s.in_progress_count;
              const mismatch = expectedTotal !== s.total_all_count;
              return (
                <div style={{
                  padding: '0.6rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem',
                  backgroundColor: mismatch ? '#fff3e0' : '#f5f5f5',
                  border: mismatch ? '1px solid #ffcc80' : '1px solid #e0e0e0',
                  color: mismatch ? '#e65100' : '#666',
                }}>
                  <strong>{s.total_all_count}</strong> total transactions: <strong>{s.completed_count}</strong> completed, <strong>{s.voided_count}</strong> voided, <strong>{s.in_progress_count}</strong> in-progress
                  {mismatch && <span style={{ marginLeft: '1rem', fontWeight: 'bold' }}>Warning: counts do not match total</span>}
                </div>
              );
            })()}

            {/* Shift Summary */}
            {data.sessions.length > 0 && (
              <div style={{
                backgroundColor: '#fff', padding: '1rem', borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem',
              }}>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>Shift Summary</h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem' }}>Cashier</th>
                        <th style={{ padding: '0.5rem' }}>Start</th>
                        <th style={{ padding: '0.5rem' }}>End</th>
                        <th style={{ padding: '0.5rem' }}>Status</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Opening Cash</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Expected</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Closing</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Variance</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Check Var.</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Petty Cash</th>
                        <th style={{ padding: '0.5rem' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sessions.map(s => (
                        <tr key={s.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '0.5rem' }}>{s.cashier}</td>
                          <td style={{ padding: '0.5rem' }}>{new Date(s.opened_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                          <td style={{ padding: '0.5rem' }}>{s.closed_at ? new Date(s.closed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          <td style={{ padding: '0.5rem' }}>
                            <span style={{
                              padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem',
                              backgroundColor: s.status === 'closed' ? '#e8f5e9' : '#fff3e0',
                              color: s.status === 'closed' ? '#2e7d32' : '#e65100',
                            }}>
                              {s.status}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>${s.opening_cash.toFixed(2)}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>{s.expected_cash != null ? `$${s.expected_cash.toFixed(2)}` : '—'}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>{s.closing_cash != null ? `$${s.closing_cash.toFixed(2)}` : '—'}</td>
                          <td style={{
                            padding: '0.5rem', textAlign: 'right', fontWeight: 'bold',
                            color: s.cash_variance != null ? (Math.abs(s.cash_variance) < 1 ? '#2e7d32' : '#d32f2f') : '#666',
                          }}>
                            {s.cash_variance != null ? `$${s.cash_variance.toFixed(2)}` : '—'}
                          </td>
                          <td style={{
                            padding: '0.5rem', textAlign: 'right', fontWeight: 'bold',
                            color: s.check_variance != null ? (Math.abs(s.check_variance) < 1 ? '#2e7d32' : '#d32f2f') : '#666',
                          }}>
                            {s.check_variance != null ? `$${s.check_variance.toFixed(2)}` : '—'}
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', color: '#508590', fontWeight: 'bold' }}>
                            {(s.petty_cash_total || 0) > 0 ? `-$${(s.petty_cash_total || 0).toFixed(2)}` : '—'}
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            {s.status === 'closed' && (
                              <button
                                onClick={() => handleReprintTillReport(s.id)}
                                disabled={reprintingSession === s.id}
                                style={{
                                  padding: '0.2rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#508590',
                                  color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer',
                                  opacity: reprintingSession === s.id ? 0.6 : 1,
                                }}
                                title="Reprint till close report"
                              >
                                {reprintingSession === s.id ? '...' : 'Reprint'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Petty Cash Withdrawals */}
            {data.petty_cash_withdrawals && data.petty_cash_withdrawals.length > 0 && (
              <div style={{
                backgroundColor: '#fff', padding: '1rem', borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem',
              }}>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 'bold', marginBottom: '0.75rem', color: '#508590' }}>
                  Petty Cash Withdrawals ({data.petty_cash_count}) &mdash; Total: ${data.petty_cash_total.toFixed(2)}
                </h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem' }}>Time</th>
                        <th style={{ padding: '0.5rem' }}>Category</th>
                        <th style={{ padding: '0.5rem' }}>Description</th>
                        <th style={{ padding: '0.5rem' }}>Withdrawn By</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.petty_cash_withdrawals.map(w => (
                        <tr key={w.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '0.5rem' }}>{w.time}</td>
                          <td style={{ padding: '0.5rem' }}>{w.category_display}</td>
                          <td style={{ padding: '0.5rem', color: '#666' }}>{w.description || '—'}</td>
                          <td style={{ padding: '0.5rem' }}>{w.withdrawn_by}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 'bold', color: '#d32f2f' }}>
                            -${w.amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Transaction Table */}
            <div style={{
              backgroundColor: '#fff', padding: '1rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              {(() => {
                const filteredTxns = data.transactions.filter(txn => {
                  if (taxFilter === 'taxable') return !txn.tax_exempt;
                  if (taxFilter === 'exempt') return txn.tax_exempt;
                  return true;
                });
                return (
                  <>
                    <h2 style={{ fontSize: '1.15rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
                      Transactions ({filteredTxns.length}{taxFilter !== 'all' ? ` of ${data.transactions.length}` : ''})
                    </h2>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                            <th style={{ padding: '0.5rem' }}>Txn #</th>
                            <th style={{ padding: '0.5rem' }}>Time</th>
                            <th style={{ padding: '0.5rem' }}>Cashier</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center' }}>Items</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Subtotal</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Tax</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Surcharge</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Total</th>
                            <th style={{ padding: '0.5rem' }}>Payment</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center' }}>Tax Exempt</th>
                            <th style={{ padding: '0.5rem' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTxns.map(txn => (
                            <React.Fragment key={txn.id}>
                              <tr
                                onClick={() => setExpandedTxn(expandedTxn === txn.id ? null : txn.id)}
                                style={{
                                  borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                                  backgroundColor: expandedTxn === txn.id ? '#f5f5f5' : 'transparent',
                                }}
                              >
                                <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{txn.transaction_number}</td>
                                <td style={{ padding: '0.5rem' }}>{txn.time}</td>
                                <td style={{ padding: '0.5rem' }}>{txn.cashier}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'center' }}>{txn.item_count}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>${txn.subtotal.toFixed(2)}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>${txn.tax.toFixed(2)}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{txn.surcharge > 0 ? `$${txn.surcharge.toFixed(2)}` : '\u2014'}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 'bold' }}>${txn.total.toFixed(2)}</td>
                                <td style={{ padding: '0.5rem' }}>{formatPaymentMethods(txn.payment_methods)}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                  {txn.tax_exempt ? (
                                    <span style={{
                                      padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem',
                                      backgroundColor: '#e8f5e9', color: '#2e7d32',
                                    }}>Exempt</span>
                                  ) : '\u2014'}
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  <span style={{
                                    padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem',
                                    backgroundColor: statusColor(txn.status) + '15',
                                    color: statusColor(txn.status),
                                  }}>
                                    {txn.status}
                                    {txn.refund_status && txn.refund_status !== 'none' && ` (${txn.refund_status})`}
                                  </span>
                                </td>
                              </tr>
                              {expandedTxn === txn.id && txn.items.length > 0 && (
                                <tr>
                                  <td colSpan={11} style={{ padding: '0.5rem 0.5rem 0.5rem 2rem', backgroundColor: '#fafafa' }}>
                                    {txn.customer_name && (
                                      <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#555' }}>
                                        Customer: <strong>{txn.customer_name}</strong>
                                      </div>
                                    )}
                                    <table style={{ width: '100%', fontSize: '0.9rem' }}>
                                      <thead>
                                        <tr style={{ color: '#666' }}>
                                          <th style={{ textAlign: 'left', padding: '0.25rem' }}>Item</th>
                                          <th style={{ textAlign: 'right', padding: '0.25rem' }}>Qty</th>
                                          <th style={{ textAlign: 'right', padding: '0.25rem' }}>Price</th>
                                          <th style={{ textAlign: 'right', padding: '0.25rem' }}>Total</th>
                                          <th style={{ textAlign: 'right', padding: '0.25rem' }}>Tax</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {txn.items.map((item, idx) => (
                                          <tr key={idx}>
                                            <td style={{ padding: '0.25rem' }}>{item.name}</td>
                                            <td style={{ padding: '0.25rem', textAlign: 'right' }}>{item.quantity}</td>
                                            <td style={{ padding: '0.25rem', textAlign: 'right' }}>${item.unit_price.toFixed(2)}</td>
                                            <td style={{ padding: '0.25rem', textAlign: 'right' }}>${item.total.toFixed(2)}</td>
                                            <td style={{ padding: '0.25rem', textAlign: 'right' }}>
                                              {item.is_tax_exempt ? (
                                                <span style={{ color: '#2e7d32', fontSize: '0.85rem' }}>Exempt</span>
                                              ) : (
                                                `$${item.tax_amount.toFixed(2)}`
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                          {filteredTxns.length === 0 && (
                            <tr>
                              <td colSpan={11} style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                                No transactions found for this date range.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          </>
        )}

        {loading && !data && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading report...</div>
        )}
      </div>
    </div>
  );
};

export default POSDailyReport;
